import { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import {
  CreateRnUzurkuvanjeThreadPayload,
  RnUzurkuvanjeReleasePayload,
  RnUzurkuvanjeThread,
  normalizeUzurkuvanjeListResponse,
  normalizeUzurkuvanjeResponse,
} from '../types/rnUzurkuvanjeRequests';

interface ThreadFilter {
  rnWorkOrder: string;
}

const apiUrl = process.env.REACT_APP_API_URL ?? '';
const baseUrl = `${apiUrl}/api/rn-uzurkuvanje-requests`;

const bumpQcNumber = (number: string): string => {
  const modern = number.match(/^(\d+)\/(\d{2})$/);
  if (modern) return `${String(Number(modern[1]) + 1).padStart(modern[1].length, '0')}/${modern[2]}`;
  const match = number.match(/^(.*)\/QC\/(\d+)$/i);
  if (!match) return '';
  return `${match[1]}/QC/${String(Number(match[2]) + 1).padStart(5, '0')}`;
};

export const useRnUzurkuvanjeRequests = () => {
  const { showToast } = useToast();
  const { t } = useTranslation();
  const [threads, setThreads] = useState<RnUzurkuvanjeThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [nextNumber, setNextNumber] = useState('');
  const [sampledSubprocessIds, setSampledSubprocessIds] = useState<string[]>([]);
  const [viewerCanApproveAsQc, setViewerCanApproveAsQc] = useState(false);
  const [intermediateRequiresQcRelease, setIntermediateRequiresQcRelease] = useState(false);
  const [viewerCanReleaseAsQc, setViewerCanReleaseAsQc] = useState(false);
  const filterRef = useRef<ThreadFilter | null>(null);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'x-auth-token': token || '', 'Content-Type': 'application/json' };
  };

  const loadThreads = useCallback(async (filter: ThreadFilter, targetPage = 1) => {
    filterRef.current = filter;
    setLoading(true);
    try {
      const response = await axios.get(baseUrl, {
        headers: authHeaders(),
        params: { rnWorkOrder: filter.rnWorkOrder, page: targetPage },
      });
      const envelope = normalizeUzurkuvanjeListResponse(response.data);
      setThreads((prev) => (targetPage === 1 ? envelope.data : [...prev, ...envelope.data]));
      setPage(envelope.page);
      setTotalPages(envelope.totalPages);
      if (targetPage === 1) {
        setSampledSubprocessIds(envelope.sampledSubprocessIds);
        setViewerCanApproveAsQc(envelope.viewerCanApproveAsQc);
        setIntermediateRequiresQcRelease(envelope.intermediateRequiresQcRelease);
        setViewerCanReleaseAsQc(envelope.viewerCanReleaseAsQc);
        if (envelope.nextNumber) {
          setNextNumber(envelope.nextNumber);
        } else {
          const maxAssigned = envelope.data
            .map((thread) => thread.number || '')
            .filter(Boolean)
            .sort((a, b) => {
              const seq = (value: string) => Number((value.match(/\/QC\/(\d+)$/i) || value.match(/^(\d+)\/\d{2}$/) || [])[1] || 0);
              return seq(b) - seq(a);
            })[0];
          if (maxAssigned) setNextNumber(bumpQcNumber(maxAssigned));
        }
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to load uzurkuvanje requests';
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const loadMore = useCallback(async () => {
    if (!filterRef.current || page >= totalPages) return;
    await loadThreads(filterRef.current, page + 1);
  }, [loadThreads, page, totalPages]);

  const createThread = useCallback(async (payload: CreateRnUzurkuvanjeThreadPayload): Promise<RnUzurkuvanjeThread | null> => {
    try {
      const response = await axios.post<unknown>(baseUrl, payload, { headers: authHeaders() });
      const thread = normalizeUzurkuvanjeResponse(response.data);
      if (!thread) {
        showToast('Failed to send uzurkuvanje', 'error');
        return null;
      }
      setThreads((prev) => [thread, ...prev]);
      const envelope = response.data as { nextNumber?: string };
      if (typeof envelope.nextNumber === 'string' && envelope.nextNumber) {
        setNextNumber(envelope.nextNumber);
      } else if (thread.number) {
        setNextNumber(bumpQcNumber(thread.number));
      }
      return thread;
    } catch (err: any) {
      const message = err.response?.status === 400
        && err.response?.data?.messageKey === 'rnUzurkuvanjeRequests.rowsNotReceived'
        ? t('rnUzurkuvanjeRequests.rowsNotReceived')
        : err.response?.data?.message || err.message || 'Failed to send uzurkuvanje';
      showToast(message, 'error');
      return null;
    }
  }, [showToast, t]);

  const addMessage = useCallback(async (threadId: string, text: string, dateTo?: string): Promise<RnUzurkuvanjeThread | null> => {
    try {
      const response = await axios.post<unknown>(`${baseUrl}/${threadId}/messages`, { text, dateTo }, { headers: authHeaders() });
      const thread = normalizeUzurkuvanjeResponse(response.data);
      if (!thread) {
        showToast('Failed to send reply', 'error');
        return null;
      }
      setThreads((prev) => prev.map((existing) => (existing._id === thread._id ? thread : existing)));
      return thread;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to send reply';
      showToast(message, 'error');
      return null;
    }
  }, [showToast]);

  const closeThread = useCallback(async (threadId: string): Promise<RnUzurkuvanjeThread | null> => {
    try {
      const response = await axios.patch<unknown>(`${baseUrl}/${threadId}/close`, {}, { headers: authHeaders() });
      const thread = normalizeUzurkuvanjeResponse(response.data);
      if (!thread) {
        showToast('Failed to close uzurkuvanje', 'error');
        return null;
      }
      setThreads((prev) => prev.map((existing) => (existing._id === thread._id ? thread : existing)));
      return thread;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to close uzurkuvanje';
      showToast(message, 'error');
      return null;
    }
  }, [showToast]);

  const approveThread = useCallback(async (threadId: string, input: { pin: string; text?: string }): Promise<RnUzurkuvanjeThread | null> => {
    try {
      const body = { pin: input.pin, text: input.text && input.text.trim() ? input.text.trim() : undefined };
      const response = await axios.post<unknown>(`${baseUrl}/${threadId}/approve`, body, { headers: authHeaders() });
      const thread = normalizeUzurkuvanjeResponse(response.data);
      if (!thread) {
        showToast('Failed to approve uzurkuvanje', 'error');
        return null;
      }
      setThreads((prev) => prev.map((existing) => (existing._id === thread._id ? thread : existing)));
      return thread;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to approve uzurkuvanje';
      showToast(message, 'error');
      return null;
    }
  }, [showToast]);

  const releaseThread = useCallback(async (threadId: string, input: RnUzurkuvanjeReleasePayload): Promise<RnUzurkuvanjeThread | null> => {
    try {
      const body = {
        decision: input.decision,
        analysisNumber: input.analysisNumber.trim(),
        inSpec: input.inSpec,
        comment: input.comment && input.comment.trim() ? input.comment.trim() : undefined,
        pin: input.pin,
      };
      const response = await axios.post<unknown>(`${baseUrl}/${threadId}/release`, body, { headers: authHeaders() });
      const thread = normalizeUzurkuvanjeResponse(response.data);
      if (!thread) {
        showToast('Failed to record QC release', 'error');
        return null;
      }
      setThreads((prev) => prev.map((existing) => (existing._id === thread._id ? thread : existing)));
      return thread;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to record QC release';
      showToast(message, 'error');
      return null;
    }
  }, [showToast]);

  const refreshNextNumber = useCallback(async (rnWorkOrder: string) => {
    try {
      const response = await axios.get(`${baseUrl}/next-number`, {
        headers: authHeaders(),
        params: { rnWorkOrder },
      });
      const payload = response.data as { data?: { number?: string }; number?: string };
      const value = payload?.data?.number || payload?.number || '';
      if (value) setNextNumber(value);
      return value;
    } catch {
      return '';
    }
  }, []);

  return {
    threads,
    loading,
    page,
    totalPages,
    nextNumber,
    sampledSubprocessIds,
    viewerCanApproveAsQc,
    intermediateRequiresQcRelease,
    viewerCanReleaseAsQc,
    setNextNumber,
    loadThreads,
    loadMore,
    createThread,
    addMessage,
    closeThread,
    approveThread,
    releaseThread,
    refreshNextNumber,
  };
};
